import { useTranslation } from 'react-i18next';
import { WarningIcon } from '@phosphor-icons/react';
import { Spinner } from '@/components/Spinner';
import { CameraStatusTooltip } from '@/components/CameraStatusTooltip';
import {
    CameraCreationItem,
    CameraCreationStatus,
    CameraValidationStatus,
} from '@/types/camera';
import { SecurityCameraIconMirrored } from '@/utils/icons';
import { I18nNamespace } from '@/lib/i18n-namespaces';

interface CameraSourceStatusDrawerItemProps {
    item: CameraCreationItem;
}

export function CameraSourceStatusDrawerItem({
    item,
}: CameraSourceStatusDrawerItemProps) {
    const { t } = useTranslation(I18nNamespace.CAMERAS);

    const isCreating = item.status === CameraCreationStatus.CREATING;
    const isCompleted = item.status === CameraCreationStatus.COMPLETED;
    const isError = item.status === CameraCreationStatus.ERROR;
    const isValidating =
        isCompleted &&
        item.validationStatus === CameraValidationStatus.VALIDATING;
    const validationDone =
        isCompleted &&
        (item.validationStatus === CameraValidationStatus.SUCCESS ||
            item.validationStatus === CameraValidationStatus.ERROR);

    const getStatusText = () => {
        if (isCreating) return t('statusDrawer.creatingCamera');
        if (isValidating) return t('statusDrawer.verifyingConnection');
        return null;
    };

    const statusText = getStatusText();

    return (
        <div className="flex items-center gap-3 p-3 border-b border-grey-700 last:border-b-0">
            <div className="flex-shrink-0 w-16 h-9 rounded bg-grey-550 flex items-center justify-center overflow-hidden">
                {item.validationSnapshot ? (
                    <img
                        src={`data:image/jpeg;base64,${item.validationSnapshot}`}
                        alt={item.name}
                        className="w-full h-full object-cover"
                    />
                ) : (
                    <SecurityCameraIconMirrored className="w-4 h-4 text-grey-300" />
                )}
            </div>

            <div className="flex-1 min-w-0">
                <p className="text-xs text-white truncate">{item.name}</p>
                {statusText && (
                    <p className="text-xs mt-1 text-grey-300">{statusText}</p>
                )}
                {isError && item.error && (
                    <p className="text-xs text-red-500 mt-1">{item.error}</p>
                )}
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
                {(isCreating || isValidating) && (
                    <Spinner size={16} variant="light" />
                )}

                {validationDone && (
                    <CameraStatusTooltip
                        live={
                            item.validationStatus ===
                            CameraValidationStatus.SUCCESS
                        }
                        errorType={item.validationErrorType}
                        side="bottom"
                    />
                )}

                {isError && (
                    <WarningIcon className="w-4 h-4 text-warning-100" />
                )}
            </div>
        </div>
    );
}
